/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Icon, Pressable } from '@components/atoms'
import { IconButton, Tooltip, Chip } from '@mui/material'

import dayjs from 'dayjs'
import { Edit, Delete, PlaylistAdd } from '@mui/icons-material'
import { blue, deepPurple, green, grey, indigo, pink, teal } from '@mui/material/colors'
import { SeasonType } from '@schemas'

interface PropsType extends SeasonType {
	onClick?: () => void
	onDelete?: () => void
	onEdit?: () => void
	/** 판매 대기열에 추가. 이미 대기열에 있거나 판매중이면 버튼을 숨긴다. */
	onAddToQueue?: () => void
	isSelected: boolean
}

export const SeasonListItem = (props: PropsType) => {
	const dateRange = `${dayjs(props.startDate).format('YYYY.MM.DD')} - ${dayjs(
		props.endDate,
	).format('YYYY.MM.DD')}`

	const soldUsdPerTargetUsd = `${props.totalUsd.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	})} / ${props.targetUsd.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	})}`
	const soldQuantityPerTargetQuantity = `${props.soldQuantity.toLocaleString()} / ${props.targetQuantity.toLocaleString()}`
	// openYn 이 status 보다 우선한다. 지금 shop 에 노출되는 시즌은 전체에서 하나뿐이고,
	// 그것을 한눈에 구분하는 것이 이 목록의 핵심이다. (SEASON_OPEN_PLAN.md §4)
	const isOpen = props.openYn === 1
	const statusTitle = isOpen
		? '판매중'
		: props.status === 'not_start'
		? '시작 전'
		: props.status === 'expired'
		? '기간 종료'
		: props.status === 'inactive'
		? '사용 중지'
		: props.status === 'sold_out'
		? '완판'
		: '활성화'
	const statusClassName = isOpen ? 'open' : props.status.toLowerCase()

	return (
		<View css={style}>
			<Pressable
				className={
					'productItemButton ' + (props.isSelected ? 'selected' : statusClassName)
				}
				onClick={props.onClick}
			>
				{props.isSelected && (
					<View className="selectedIndicatorView">
						<Icon name="check-bold" className="selectedIndicatorIcon" />
					</View>
				)}
				<View className="buttonContainer">
					{!isOpen && props.queueOrder === null && (
						<Tooltip title="판매 대기열에 추가">
							<IconButton
								size="small"
								onClick={e => {
									e.stopPropagation()
									props.onAddToQueue?.()
								}}
							>
								<PlaylistAdd fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					<Tooltip title="Edit">
						<IconButton
							size="small"
							onClick={e => {
								e.stopPropagation()
								props.onEdit?.()
							}}
						>
							<Edit fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete">
						<IconButton
							size="small"
							onClick={e => {
								e.stopPropagation()
								props.onDelete?.()
							}}
						>
							<Delete fontSize="small" />
						</IconButton>
					</Tooltip>
				</View>

				<View className="contentView">
					<View className="header">
						<Text className="title">{props.title}</Text>
						<Chip
							label={statusTitle}
							size="small"
							className={`status ${statusClassName}`}
						/>
					</View>

					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							판매기간 :
						</Text>
						<Text category="p2" className="contentInfoValue">
							{dateRange}
						</Text>
					</View>
					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							판매금액 :
						</Text>
						<Text category="p2" className="contentInfoValue">
							{soldUsdPerTargetUsd}
						</Text>
					</View>
					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							판매수량 :
						</Text>
						<Text category="p2" className="contentInfoValue">
							{soldQuantityPerTargetQuantity}
						</Text>
					</View>
				</View>
			</Pressable>
		</View>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	marginBottom: '0.75rem',

	'.productItemButton': {
		width: '100%',
		height: 'fit-content',
		padding: '0.75rem',
		borderRadius: '5px',
		boxShadow: '0px 2px 5px 0px rgb(0 0 0 / 25%)',
		borderLeft: '10px solid',
		transition: 'all 0.2s ease-in-out',
		background: 'white',

		'&:hover': {
			backgroundColor: '#f9fafb',
			boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
		},

		'&.open': {
			borderLeftColor: green['A700'],
			borderLeftWidth: '6px',
		},

		'&.active': {
			borderLeftColor: blue['A400'],
		},

		'&.not_start': {
			borderLeftColor: teal['A400'],
		},

		'&.sold_out': {
			borderLeftColor: pink['A400'],
		},

		'&.expired': {
			borderLeftColor: grey[400],
		},

		'&.inactive': {
			borderLeftColor: grey[400],
		},

		'&.selected': {
			borderColor: deepPurple['A400'],
			borderLeftColor: deepPurple['A400'],
			background: '#eee7f6',

			'.contentView': {
				'.header': {
					'.title': {
						color: deepPurple['A400'],
					},

					'.status': {
						backgroundColor: deepPurple['A400'] + ' !important',
						color: 'white !important',
					},
				},
			},

			'.contentInfoView': {
				'.contentInfoLabel': {
					color: indigo[500],
				},

				'.contentInfoValue': {
					color: deepPurple['A400'],
				},
			},
		},

		'.selectedIndicatorView': {
			position: 'absolute',
			width: '1.3rem',
			height: '1.3rem',
			top: 0,
			bottom: 0,
			left: '-0.5rem',
			margin: 'auto',
			background: 'white',
			border: '1px solid',
			borderColor: deepPurple['A400'],
			borderRadius: '50%',

			'.selectedIndicatorIcon': {
				color: deepPurple['A400'],
				fontSize: '18px',
			},
		},

		'.buttonContainer': {
			position: 'absolute',
			top: '0.75rem',
			right: '0.75rem',
			display: 'flex',
			gap: '0.25rem',
		},
		'.contentView': {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',

			'.header': {
				display: 'flex',
				gap: '0.5rem',
				alignItems: 'center',
				paddingRight: '4rem',
				height: '2.25rem',

				'.title': {
					fontSize: '1.25rem',
					fontWeight: 600,
					color: '#111827',
				},

				'.status': {
					fontSize: '0.75rem',
					fontWeight: 900,

					// 판매중은 목록에서 하나뿐이므로 채운 배경으로 확실히 구분한다
					'&.open': {
						backgroundColor: green['A700'],
						color: 'white',
					},
					'&.active': {
						backgroundColor: blue[50],
						color: blue['A700'],
					},
					'&.not_start': {
						backgroundColor: teal[50],
						color: teal['A700'],
					},
					'&.sold_out': {
						backgroundColor: pink[50],
						color: pink['A700'],
					},
					'&.expired': {
						backgroundColor: grey[50],
						color: grey[900],
					},
					'&.inactive': {
						backgroundColor: grey[50],
						color: grey[900],
					},
				},
			},

			'.contentInfoView': {
				display: 'flex',
				alignItems: 'center',
				gap: '0.25rem',
				height: '1rem',

				'.contentInfoLabel': {
					margin: 0,
				},
				'.contentInfoValue': {
					margin: 0,
				},
			},
		},
	},
})
